import { Controller } from "@hotwired/stimulus"
import { savePlan, replacePlan } from "plan_storage"
import { clearIdbBackup } from "plan_backup"
import { renderIcons } from "lucide_icons"

// Editor toolbar controller — wires the chrome buttons to actions.
//
// Plan data stays in localStorage per DFLW-001B. PDF generation runs
// the browser's native print() against the .print-page sections the
// preview controller has already laid out on this page, so the PDF
// matches exactly what the user sees in /editor/native/preview.
//
// The browser's print dialog lists every available destination — Save
// as PDF, the OS's default printer, and any networked / "server"
// printers the OS knows about. There's no /api/generate-pdf round-trip
// (the helper would have rendered the legacy pcp_builder.html print
// view instead of the native preview, which is the wrong page).
//
// Workspace branding (window.AAVIOND_ORG) is fetched once on connect
// from /api/branding and refreshed every 5 seconds — matching the live-
// poll behavior of the iframe editor (the preview controller reads it
// from this controller's `orgInfo` to render the side strip).
const STORAGE_KEY = "pcp_plan_data"

export default class extends Controller {
  static targets = ["pdfBtn", "saveBtn"]

  connect() {
    this.orgInfo = null
    this.fetchBranding()
    this.brandingTimer = setInterval(() => this.fetchBranding(), 5000)
  }

  disconnect() {
    clearInterval(this.brandingTimer)
  }

  // Scan the plan for the participant's first name, last name, and
  // current age and offer to replace each occurrence with a
  // placeholder token ([FirstName] / [LastName] / [Age]). Lets the
  // user turn an authored plan into a re-usable template — or
  // anonymise it for sharing — without hand-editing every field.
  // Start a fresh PCP — wipes localStorage and broadcasts a plan:change
  // event so every controller re-renders into its empty state.
  async newPlan(event) {
    event?.preventDefault?.()
    // Data-loss guard (issue #12): starting a new PCP wipes the current plan,
    // so WARN, OFFER to save a backup, and CONFIRM TWICE before clearing.
    // 1) First warning — Cancel keeps the current plan.
    if (!window.confirm(
      "⚠ Start a NEW PCP?\n\n" +
      "This permanently clears ALL data in the current plan and cannot be undone.\n\n" +
      "Click Cancel to keep your current plan."
    )) return
    // 2) Offer to save an encrypted .sdp backup first.
    if (window.confirm(
      "Save a backup of the current plan first?\n\n" +
      "OK  → download an encrypted .sdp copy now.\n" +
      "Cancel → continue without saving."
    )) {
      try {
        const el  = document.querySelector('[data-controller~="sdp"]')
        const sdp = el && this.application.getControllerForElementAndIdentifier(el, "sdp")
        if (sdp && typeof sdp.save === "function") await sdp.save()   // awaits encrypt + download (or modal cancel)
        else window.alert("Use the Save (.sdp) button in the toolbar to keep a copy, then start a new PCP.")
      } catch (_) { /* if saving errors/cancels, fall through to the final confirm */ }
    }
    // 3) Final confirmation (ask twice).
    if (!window.confirm(
      "Last check — permanently erase the current plan and start a blank one?"
    )) return
    try { localStorage.removeItem(STORAGE_KEY) } catch (_) {}
    // Also clear the IndexedDB mirror — otherwise it restores the plan on the
    // next load and "New PCP" appears to do nothing. Await so the delete lands
    // before the reload below.
    await clearIdbBackup()
    const plan = {}
    document.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true,
      detail: { path: "*", plan, source: "newPlan" }
    }))
    // Soft reload so URL state, focused sections, and dynamic-lists
    // reset cleanly without us hand-clearing every controller's state.
    window.location.reload()
  }

  // Load the bundled sample PCP so the user can see a populated plan
  // (every section filled in) without having to author one. Same
  // localStorage key as a real plan — round-trip safe.
  async loadSample(event) {
    event?.preventDefault?.()
    const ok = window.confirm(
      "Load the sample PCP?\n\nThis replaces the current plan in this browser " +
      "with our worked example. Save the current plan as an .sdp first if you want to keep it."
    )
    if (!ok) return
    try {
      const res = await fetch("/sample.pcp.json", { credentials: "same-origin" })
      if (!res.ok) throw new Error("HTTP " + res.status)
      const plan = await res.json()
      replacePlan(plan)
      window.location.reload()
    } catch (e) {
      window.alert("Couldn't load the sample PCP: " + (e?.message || e))
    }
  }

  templatize(event) {
    event?.preventDefault?.()
    let plan
    try { plan = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") }
    catch (_) { plan = {} }
    const client = plan.client || {}
    const first  = (client.firstName || "").trim()
    const last   = (client.lastName  || "").trim()
    const age    = this._computeAge(client.dob)
    if (!first && !last && age == null) {
      alert("Set the participant's First Name, Last Name, or Date of Birth on the Client section before scanning.")
      return
    }
    const targets = []
    if (first)          targets.push({ kind: "first", needle: first, token: "[FirstName]" })
    if (last)           targets.push({ kind: "last",  needle: last,  token: "[LastName]" })
    if (age != null)    targets.push({ kind: "age",   needle: String(age), token: "[Age]", ageMode: true })
    // Dry-run on a deep clone so the on-disk plan is untouched until
    // the user confirms.
    const counts = { first: 0, last: 0, age: 0 }
    const previewPlan = JSON.parse(JSON.stringify(plan))
    this._walkPlanStrings(previewPlan, (s, set) => {
      let out = s
      targets.forEach(t => {
        const rx = t.ageMode
          ? new RegExp(`\\b${t.needle}\\b(?=\\s*(?:years?\\s*old|yrs?\\s*old|yr\\b|years?\\b|y\\.?\\s*o\\.?\\b))`, "gi")
          : new RegExp(`\\b${this._escapeRx(t.needle)}\\b`, "gi")
        out = out.replace(rx, () => { counts[t.kind]++; return t.token })
      })
      if (out !== s) set(out)
    }, this._templatizeSkip(previewPlan))
    const summary = [
      first ? `${counts.first} matches of "${first}" → [FirstName]` : "",
      last  ? `${counts.last} matches of "${last}" → [LastName]`    : "",
      age != null ? `${counts.age} matches of "${age}" (in age context) → [Age]` : ""
    ].filter(Boolean).join("\n")
    if (!counts.first && !counts.last && !counts.age) {
      alert("No occurrences of the participant's name or age were found in the plan.")
      return
    }
    if (!confirm(`Replace the following throughout the plan?\n\n${summary}\n\nThe Client, Revision and About sections (which define these values) are left unchanged. Undo with Ctrl+Z.`)) {
      return
    }
    // Apply for real this time.
    const final = { ...counts }
    final.first = 0; final.last = 0; final.age = 0
    this._walkPlanStrings(plan, (s, set) => {
      let out = s
      targets.forEach(t => {
        const rx = t.ageMode
          ? new RegExp(`\\b${t.needle}\\b(?=\\s*(?:years?\\s*old|yrs?\\s*old|yr\\b|years?\\b|y\\.?\\s*o\\.?\\b))`, "gi")
          : new RegExp(`\\b${this._escapeRx(t.needle)}\\b`, "gi")
        out = out.replace(rx, () => { final[t.kind]++; return t.token })
      })
      if (out !== s) set(out)
    }, this._templatizeSkip(plan))
    savePlan(plan)
    document.dispatchEvent(new CustomEvent("plan:change", {
      bubbles: true,
      detail: { path: "*", plan, source: "templatize" }
    }))
  }

  // Walks every string value reachable through the plan tree. For
  // each one, calls fn(currentValue, setter). The setter accepts the
  // replacement string and writes it back into the parent.
  // Optional shouldSkip(parentNode, key) returns true to prune a subtree
  // (or string) — used by templatize to leave the tag-defining sections
  // (Client / Revision / the About fields) untouched.
  _walkPlanStrings(node, fn, shouldSkip) {
    if (!node || typeof node !== "object") return
    if (Array.isArray(node)) {
      node.forEach((v, i) => {
        if (shouldSkip && shouldSkip(node, i)) return
        if (typeof v === "string") fn(v, (nv) => { node[i] = nv })
        else if (v && typeof v === "object") this._walkPlanStrings(v, fn, shouldSkip)
      })
      return
    }
    Object.keys(node).forEach(k => {
      if (shouldSkip && shouldSkip(node, k)) return
      const v = node[k]
      if (typeof v === "string") fn(v, (nv) => { node[k] = nv })
      else if (v && typeof v === "object") this._walkPlanStrings(v, fn, shouldSkip)
    })
  }

  // Build the templatize skip predicate for a given plan root. Prunes the
  // sections that DEFINE the token values so we don't overwrite the
  // source of truth (e.g. turn client.firstName "Noah" into "[FirstName]"):
  //   • plan.client       — First/Last name, DOB → [FirstName]/[LastName]/[Age]
  //   • plan.facilitator  — the Revision section
  //   • plan.about.{whoLivesWithMe, communicationStyle, schoolDetails,
  //                 workDetails} — the About section's own fields ([School],
  //                 [Location] sources). NOTE: circleOfSupport and
  //                 communicationChart live under plan.about too but are
  //                 SEPARATE sections, so they are NOT skipped.
  _templatizeSkip(root) {
    const about = root && root.about
    const rootSkip  = new Set(["client", "facilitator"])
    const aboutSkip = new Set(["whoLivesWithMe", "communicationStyle", "schoolDetails", "workDetails"])
    return (node, key) => {
      if (node === root && rootSkip.has(key)) return true
      if (about && node === about && aboutSkip.has(key)) return true
      return false
    }
  }

  _escapeRx(s) { return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&") }

  // Returns the participant's current age in whole years from their
  // DOB (ISO date). null when the DOB is missing or invalid.
  _computeAge(dob) {
    if (!dob || !/^\d{4}-\d{2}-\d{2}/.test(dob)) return null
    const d = new Date(dob)
    if (isNaN(d.getTime())) return null
    const now = new Date()
    let years = now.getFullYear() - d.getFullYear()
    const before = now.getMonth() < d.getMonth() ||
                   (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())
    if (before) years--
    return years >= 0 ? years : null
  }

  async fetchBranding() {
    try {
      const r = await fetch("/api/branding", {
        credentials: "same-origin",
        headers: { Accept: "application/json" }
      })
      if (r.ok) this.orgInfo = await r.json()
    } catch (_) { /* offline-tolerant */ }
  }

  // Save as PDF — POSTs the plan to /api/generate-native-pdf. The
  // server caches the plan, spawns headless Chromium, navigates it to
  // /internal/native_pdf_render (the same native preview, plan
  // pre-injected so it doesn't depend on the user's localStorage),
  // captures the rendered pages as a Letter PDF, streams the bytes
  // back. Shift+click falls back to the browser's print dialog so
  // the user can still pick a physical printer or override.
  async generatePdf(event) {
    event?.preventDefault()
    const plan = this._loadPlan()
    if (!plan || !plan.client) {
      this._notify("Add some client info first.", "warn")
      return
    }

    const { nextRevision } = this._computeNextRevision(plan)
    const pdfFilename = this._pdfFilename(plan, nextRevision)

    if (event?.shiftKey) {
      return this._printViaDialog(plan, nextRevision, pdfFilename, event)
    }
    return this._renderViaServer(plan, nextRevision, pdfFilename, event)
  }

  // Save as PDF (Legacy) — admin-only. POSTs to /api/generate-pdf which
  // uses the iframe-era pcp_builder.html render path (same pipeline the
  // desktop Puppeteer sidecar used). Surfaced as a separate button so
  // admins have a fallback while quirks in the native PDF surface get
  // polished. Same plan + same filename + same revision bookkeeping —
  // only the server endpoint differs.
  async generateLegacyPdf(event) {
    event?.preventDefault()
    const plan = this._loadPlan()
    if (!plan || !plan.client) {
      this._notify("Add some client info first.", "warn")
      return
    }
    const { nextRevision } = this._computeNextRevision(plan)
    const pdfFilename = this._pdfFilename(plan, nextRevision)
    return this._renderViaServer(plan, nextRevision, pdfFilename, event, "/api/generate-pdf")
  }

  // Server-side path. POSTs the plan to /api/generate-native-pdf (or
  // a caller-supplied alternate endpoint, e.g. /api/generate-pdf for
  // the admin-only legacy path), downloads the returned PDF blob,
  // bumps the revision on success. On any error the user gets a
  // notify + offer to retry via the print dialog (Shift-click).
  async _renderViaServer(plan, nextRevision, pdfFilename, event, endpoint = "/api/generate-native-pdf") {
    const btn = event?.currentTarget || (this.hasPdfBtnTarget ? this.pdfBtnTarget : null)
    const prev = btn?.innerHTML
    if (btn) { btn.disabled = true; btn.innerHTML = '<i data-lucide="loader-2" class="animate-spin"></i>' }
    renderIcons(this.element)
    this._notify("Generating PDF…", "ok")

    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(endpoint, {
        method:  "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept":       "application/pdf",
          ...(csrf ? { "X-CSRF-Token": csrf } : {})
        },
        credentials: "same-origin",
        body: JSON.stringify({ plan })
      })
      if (!res.ok) {
        const detail = (await res.text().catch(() => ""))?.slice(0, 240)
        throw new Error(`server error ${res.status}${detail ? ": " + detail : ""}`)
      }
      const blob = await res.blob()
      this._downloadBlob(blob, pdfFilename)

      this._recordRevision(plan, nextRevision, "PDF exported")
      this._savePlan(plan)
      this._notify(`PDF saved — Revision ${nextRevision}`, "ok")
    } catch (err) {
      console.error("[editor-toolbar] server PDF failed:", err)
      this._notify(`PDF generation failed — ${err.message}. Shift-click for the print dialog.`, "err")
    } finally {
      if (btn) { btn.disabled = false; btn.innerHTML = prev }
      renderIcons(this.element)
    }
  }

  _downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    // Revoking too eagerly cancels the download in some browsers; one
    // tick is enough for the dialog to take ownership of the blob.
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  // Browser print dialog path — preserved so Shift-click still works
  // when the user wants to pick a physical printer or override the
  // server output. Same bookkeeping as the server path on a real
  // afterprint.
  async _printViaDialog(plan, nextRevision, pdfFilename, event) {
    const btn = this.hasPdfBtnTarget ? this.pdfBtnTarget : event?.currentTarget
    const prev = btn?.innerHTML
    if (btn) { btn.disabled = true; btn.innerHTML = '<i data-lucide="loader-2" class="animate-spin"></i>' }
    renderIcons(this.element)

    const prevTitle = document.title
    document.title = pdfFilename.replace(/\.pdf$/i, "")
    document.body.classList.add("generating-pdf")
    this._notify("Opening print dialog…", "ok")

    let printed = false
    const onAfterPrint = () => { printed = true }
    window.addEventListener("afterprint", onAfterPrint, { once: true })

    const restore = () => {
      document.title = prevTitle
      document.body.classList.remove("generating-pdf")
      if (btn) { btn.disabled = false; btn.innerHTML = prev }
      renderIcons(this.element)
      window.removeEventListener("afterprint", onAfterPrint)
      if (printed) {
        this._recordRevision(plan, nextRevision, "PDF exported")
        this._savePlan(plan)
        this._notify(`PDF saved — Revision ${nextRevision}`, "ok")
      }
    }
    window.addEventListener("afterprint", restore, { once: true })

    try {
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)))
      window.print()
    } finally {
      setTimeout(() => { if (!printed) { printed = true; restore() } }, 1500)
    }
  }

  _loadPlan() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? JSON.parse(raw) : null
    } catch (_) { return null }
  }

  _savePlan(plan) {
    savePlan(plan)
    // Same broadcast Stimulus controllers listen to so the editor's
    // open tabs refresh after a revision bump.
    document.dispatchEvent(new CustomEvent("plan:change", { detail: { source: "editor-toolbar" } }))
  }

  // pcp_builder.html#4424 — last revision in the history → bump minor →
  // zero-pad minor to 2 digits → "MAJOR.MM".
  _computeNextRevision(plan) {
    if (!plan.meta) plan.meta = {}
    if (!Array.isArray(plan.meta.revisionHistory)) plan.meta.revisionHistory = []
    let last = ""
    for (let i = plan.meta.revisionHistory.length - 1; i >= 0; i--) {
      if (plan.meta.revisionHistory[i] && plan.meta.revisionHistory[i].revision) {
        last = plan.meta.revisionHistory[i].revision
        break
      }
    }
    if (!last) last = plan.meta.revision || "1.00"
    const parts = String(last).split(".")
    const major = parts[0] || "1"
    const minor = parseInt(parts[1]) || 0
    const nextMinor = String(minor + 1).padStart(2, "0")
    return { nextRevision: `${major}.${nextMinor}` }
  }

  _recordRevision(plan, revision, comments) {
    if (!plan.meta) plan.meta = {}
    if (!Array.isArray(plan.meta.revisionHistory)) plan.meta.revisionHistory = []
    plan.meta.revisionHistory.push({
      id: Date.now(),
      revision,
      date: new Date().toISOString().split("T")[0],
      comments
    })
    plan.meta.revision = revision
    plan.meta.lastModified = new Date().toISOString()
  }

  // Filename pattern matches pcp_builder.html#4441 exactly:
  //   "Person Centered Plan - <Name>, Revision <X.YY>.pdf"
  // Redacted plans use "Redacted" in place of the participant's name.
  _pdfFilename(plan, revision) {
    const redacted = !!plan.meta?.redacted
    const name = redacted
      ? "Redacted"
      : (plan.client?.name
          || `${plan.client?.firstName || ""} ${plan.client?.lastName || ""}`.trim()
          || "Participant").trim()
    const rev = revision || plan.meta?.revision || "1.00"
    return `Person Centered Plan - ${name}, Revision ${rev}.pdf`
  }

  _notify(msg, kind = "ok") {
    // Lightweight toast; full notification system arrives with the
    // toolbar UX port (see playbook).
    let bar = document.getElementById("en-toast")
    if (!bar) {
      bar = document.createElement("div")
      bar.id = "en-toast"
      bar.style.cssText = "position:fixed;top:12px;right:12px;padding:10px 14px;border-radius:8px;font:600 13px/1.2 ui-sans-serif,system-ui;z-index:9999;box-shadow:0 8px 24px rgba(0,0,0,.18);pointer-events:none;"
      document.body.appendChild(bar)
    }
    bar.style.background = kind === "err" ? "#fee2e2" : kind === "warn" ? "#fef3c7" : "#dcfce7"
    bar.style.color      = kind === "err" ? "#991b1b" : kind === "warn" ? "#92400e" : "#166534"
    bar.textContent = msg
    bar.style.opacity = "1"
    clearTimeout(this._toastTimer)
    this._toastTimer = setTimeout(() => { bar.style.opacity = "0" }, 3500)
  }
}
